/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import { cloneDeep, isEmpty } from 'lodash';
import Types from '../constants/actionTypes';

const savedUser = () => {
  let user = localStorage.getItem('adzUser');
  try {
    user = JSON.parse(user);
  } catch (err) {
    user = {};
  }

  return user || {};
};

const tokenValid = (expiry) => {
  const tokenExpiry = new Date(parseInt(expiry, 10) * 1000);
  return tokenExpiry > new Date();
};

const loginStatus = () => {
  let status = false;
  const user = savedUser();
  if (user.token && tokenValid(user.expiry)) status = true;
  // TODO: checkRememberMe(savedUser.remember)
  return status;
};

const getUser = (user, users) => users.find((u) => u.email === user.uid) || {};

const initialState = {
  initialDataLoading: true,
  user: savedUser(),
  users: [],
  isLoggedIn: loginStatus(),
  isFetching: false,
  isUploading: false,
  resetPasswordErrors: {},
  loginErrors: null,
  userGot401: false,
  activeSpacesSpinner: false
};

const updateAllUsers = (state, newUsers) => {
  if (isEmpty(state.users)) return newUsers;

  return state.users.map((user) => {
    const newUser = newUsers.find((u) => u.id === user.id);
    if (newUser) return { ...user, ...newUser };

    return user;
  });
};

const userReducer = (state = initialState, action) => {
  const { user, teams } = action;
  let users;
  let updatedUsers;
  let newCurrentUser;
  let userClone;
  switch (action.type) {
    case Types.REQUEST_USER:
    case Types.CREATE_USER:
    case Types.REQUEST_USER_UPDATE:
      return { ...state, isFetching: true };
    case Types.REQUEST_USER_FAILED:
      return { ...state, isFetching: false };
    case Types.UPLOAD_PROFILE_PICTURE:
      return { ...state, isUploading: true };
    case Types.USER_NOT_FETCHING:
      return { ...state, isFetching: false };
    case Types.UPDATE_USER_ERROR:
      return {
        ...state,
        isUploading: false,
        isFetching: false,
        formError: action.formError || {}
      };
    case Types.RECEIVE_USER:
      return {
        ...state,
        isFetching: false,
        isUploading: false,
        users: updateAllUsers(state, action.users),
        formError: null,
        user: { ...state.user, ...getUser(state.user, action.users) },
        initialDataLoading: false
      };
    case Types.RECEIVE_TEAMS:
      // when users are retrieved ahead of teams, match the user team ids to the teams
      // eslint-disable-next-line no-case-declarations
      const newUsers = state.users.map((u) => ({
        ...u,
        teams: u.team_ids
          ? teams.filter((t) => u.team_ids.some((tid) => tid === t.id))
          : teams.filter((t) => u.teams.some((ut) => ut.id === t.id)) // structure of current serialized user
      }));
      // eslint-disable-next-line no-case-declarations
      newCurrentUser = getUser(state.user, newUsers);
      newCurrentUser.organizations = state.user.organizations; // ensure current user retains full org data including permissions
      return {
        ...state,
        users: newUsers,
        user: { ...state.user, newCurrentUser }
      };
    case Types.ADD_USER:
      return {
        ...state,
        users: state.users.concat(action.user)
      };
    case Types.GET_USER_PROFILE:
      users = state.users.map((oldUser) => {
        if (oldUser.id === user.id) return { ...oldUser, ...user };
        return oldUser;
      });

      if (user.id === state.user.id) {
        newCurrentUser = {
          ...state.user,
          ...user
        };
      } else {
        newCurrentUser = { ...state.user };
      }
      newCurrentUser.organizations = state.user.organizations; // ensure current user retains full org data including permissions

      return {
        ...state,
        users,
        isFetching: false,
        isUploading: false,
        formError: null,
        user: newCurrentUser
      };
    case Types.UPDATE_USER:
      users = state.users.map((oldUser) => {
        if (oldUser.id === user.id) return { ...oldUser, ...user };
        return oldUser;
      });
      return {
        ...state,
        users,
        isFetching: false,
        isUploading: false,
        formError: null
      };
    case Types.AUTHORIZED_PRODUCT_IDS:
      users = state.users.map((oldUser) => {
        if (oldUser.id === action.userId) {
          return { ...oldUser, authorizedProductIds: action.productIds };
        }
        return oldUser;
      });

      if (state.user.id === action.userId) {
        userClone = cloneDeep(state.user);
        userClone.authorizedProductIds = action.productIds;
      }

      return {
        ...state,
        users,
        user: userClone || state.user
      };
    case Types.USER_AUTH_UPDATE:
      user.token = user.token || state.user?.token;
      user.expiry = user.expiry || state.user?.expiry;
      localStorage.setItem('adzUser', JSON.stringify(user));
      return {
        ...state,
        isLoggedIn: loginStatus(),
        formError: null,
        user: { ...state.user, ...user }
      };
    case Types.USER_LOGIN:
      return {
        ...state,
        isFetching: false,
        userGot401: false
      };
    case Types.USER_LOGOUT:
      localStorage.setItem('adzUser', '');
      localStorage.setItem('planwallInitialState', '');
      sessionStorage.setItem('currentTeams', []);
      sessionStorage.setItem('currentProducts', []);
      return {
        ...state,
        isFetching: false,
        isLoggedIn: false,
        formError: null
      };
    case Types.UPDATE_USER_CAPACITY:
      users = state.users.map((oldUser) => {
        if (oldUser.id === action.userId) {
          return { ...oldUser, capacity: action.capacity };
        }

        return oldUser;
      });

      return {
        ...state,
        isFetching: false,
        users
      };
    case Types.RESET_PASSWORD_ERRORS:
      return {
        ...state,
        isFetching: false,
        resetPasswordErrors: action.errors
      };
    case Types.LOGIN_ERRORS:
      return {
        ...state,
        isFetching: false,
        loginErrors: action.errors
      };
    case Types.USER_GOT_401:
      return {
        ...state,
        isFetching: false,
        userGot401: true
      };
    case Types.RESET_USER_401:
      return {
        ...state,
        isFetching: false,
        userGot401: false
      };
    case Types.SPACES_SPINNER:
      return {
        ...state,
        activeSpacesSpinner: action.activeSpacesSpinner
      };

    case Types.UPDATE_SAVED_KANBAN_FILTERS:
      users = state.users.map((user) => {
        if (user.id === state.user.id) {
          userClone = {
            ...state.user,
            saved_space_view: {
              ...user.saved_space_view,
              kanban_state: action.filter,
              saved_kanban_filters: action.views
            }
          };
          return userClone;
        }
        return user;
      });
      return {
        ...state,
        user: userClone,
        users
      };
    case Types.UPDATE_USER_LOCATION:
      users = state.users.map((user) => {
        if (user.id === state.user.id) {
          userClone = {
            ...state.user,
            saved_space_view: {
              ...user.saved_space_view,
              user_location: { path: action.userLocation }
            }
          };
          return userClone;
        }
        return user;
      });
      return {
        ...state,
        user: userClone,
        users
      };
    case Types.USER_WELCOMED:
      // eslint-disable-next-line no-case-declarations
      const tourState = state.user?.saved_space_view.tour_state || {};
      users = state.users.map((user) => {
        if (user.id === state.user.id) {
          userClone = {
            ...state.user,
            saved_space_view: {
              ...user.saved_space_view,
              tour_state: { ...tourState, welcomed: true }
            }
          };
          return userClone;
        }
        return user;
      });
      return {
        ...state,
        user: userClone,
        users
      };
    case Types.CLEAR_ACTIVE_SPINNER:
      return {
        ...state,
        isFetching: false,
        isUploading: false,
        initialDataLoading: false,
        activeSpacesSpinner: false
      };
    default:
      return state;
  }
};

export default userReducer;
